import type { Entry, UnresolvedLink } from 'contentful';
import type { Cta } from 'libs/contentful/types';
import { toInternalPage, type InternalPageSkeleton } from 'libs/contentful/toInternalPage';
import type { ButtonVariant } from 'components/shared/Buttons/BaseButton';
import { LinkTargets } from 'types/constants';
import type { CtaRuntimeEntry, CtaSkeleton } from './types';

/** An inline rich-text CTA arrives as a resolved entry whose fields are not yet decoded. */
interface RawCtaEntry {
  sys: { id: string };
  fields: Record<string, unknown>;
}

function toButtonVariant(value: unknown): ButtonVariant | undefined {
  switch (value) {
    case 'Solid':
      return 'solid';
    case 'Outline':
      return 'outline';
    case 'Link':
      return 'link';
    case 'Solid Inverse':
      return 'solidInverse';
    default:
      return undefined;
  }
}

function toLinkTarget(value: unknown): LinkTargets | undefined {
  return value === LinkTargets.SameTab || value === LinkTargets.NewTab ? value : undefined;
}

/** `toInternalPage` re-checks `fields.slug`, so only the `sys.id` it reads unguarded is verified here. */
function isInternalPageLink(
  value: unknown,
): value is Entry<InternalPageSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'> | UnresolvedLink<'Entry'> {
  return (
    typeof value === 'object' &&
    value != null &&
    'sys' in value &&
    typeof value.sys === 'object' &&
    value.sys != null &&
    'id' in value.sys &&
    typeof value.sys.id === 'string'
  );
}

/** REST may retain archived CTA targets as raw Links even with `WITHOUT_UNRESOLVABLE_LINKS`. */
export function toCta(
  cta:
    | Entry<CtaSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>
    | CtaRuntimeEntry
    | RawCtaEntry
    | UnresolvedLink<'Entry'>
    | undefined,
): Cta | undefined {
  if (cta == null || !('fields' in cta)) {
    return undefined;
  }

  const { fields, sys } = cta;
  if (typeof fields.text !== 'string') {
    return undefined;
  }

  const internalPage = isInternalPageLink(fields.internalPage) ? toInternalPage(fields.internalPage) : undefined;
  const target = toLinkTarget(fields.target);
  const variant = toButtonVariant(fields.variant);

  return {
    sys: { id: sys.id },
    text: fields.text,
    ...(typeof fields.url === 'string' && { url: fields.url }),
    ...(internalPage && { internalPage }),
    ...(target && { target }),
    ...(variant && { variant }),
  };
}
